
import React, { useMemo } from 'react';
import { Calendar } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useGoogleTranslate } from '@/hooks/useTranslation';
import { useDirection } from '@/providers/DirectionProvider';

interface ArticleCardProps {
  id: string;
  title: string;
  summary: string;
  image: string;
  date: string;
  category?: string;
  author?: string;
}

const ArticleCard: React.FC<ArticleCardProps> = ({
  id,
  title,
  summary,
  image,
  date,
  category,
  author
}) => {
  const { t, i18n } = useTranslation();
  const { direction } = useDirection();
  const [imgError, setImgError] = React.useState(false);

  const isHebrew = i18n.language === 'he';

  // Prepare texts for translation
  const textsToTranslate = useMemo(() => [
    title,
    summary,
    author || ''
  ], [title, summary, author]);

  // Translate texts when not in Hebrew
  const { translatedTexts, isLoading: translating } = useGoogleTranslate({
    texts: textsToTranslate,
    enabled: !isHebrew
  });

  // Extract translated values
  const translatedTitle = translatedTexts[0] || title;
  const translatedSummary = translatedTexts[1] || summary;
  const translatedAuthor = author ? (translatedTexts[2] || author) : undefined;

  const getCategoryLabel = (categoryValue: string): string => {
    return t(`articles.categories.${categoryValue}`, t('articles.categories.default'));
  };

  const categoryLabel = category ? getCategoryLabel(category) : t('articles.categories.default');

  const handleImageError = () => {
    setImgError(true);
  };

  // Use a reliable local placeholder image or a reliable external source
  const imageUrl = imgError || !image || image.includes('via.placeholder.com')
    ? '/placeholder.svg'
    : image;

  return (
    <div className="glass-card overflow-hidden group h-full flex flex-col shadow-md hover:shadow-lg transition-all duration-300">
      <div className="relative h-48 overflow-hidden">
        <img
          src={imageUrl}
          alt={translatedTitle}
          width={628}
          height={192}
          className="w-full h-full transition-transform duration-500 group-hover:scale-105 object-cover"
          onError={handleImageError}
          loading="lazy"
        />
        {categoryLabel && (
          <div className={`absolute top-3 ${direction === 'rtl' ? 'right-3' : 'left-3'} bg-gradient-to-r from-teal-500 to-teal-400 text-white text-xs px-3 py-1.5 rounded-full shadow-sm`}>
            {categoryLabel}
          </div>
        )}
      </div>

      <div className="p-5 flex-grow flex flex-col">
        <div className="flex items-center justify-between text-gray-500 text-xs mb-2">
          <div className="flex items-center">
            <Calendar size={14} className={direction === 'rtl' ? 'ml-1' : 'mr-1'} />
            <span>{date}</span>
          </div>
          {translatedAuthor && <span className="text-gray-600">{t('articles.card.by')} {translatedAuthor}</span>}
        </div>

        <h3 className="text-lg font-semibold mb-2 line-clamp-2 group-hover:text-teal-500 transition-colors">
          {translatedTitle}
        </h3>

        <p className="text-gray-600 text-sm line-clamp-3 mb-4">
          {translatedSummary}
        </p>

        <Link to={`/articles/${id}`} className="mt-auto text-teal-500 font-medium text-sm hover:text-teal-600 inline-flex items-center">
          {t('articles.card.readMore')}
          <span className={`${direction === 'rtl' ? 'mr-1' : 'ml-1'} transform group-hover:${direction === 'rtl' ? 'translate-x-1' : '-translate-x-1'} transition-transform`}>{direction === 'rtl' ? '←' : '→'}</span>
        </Link>
      </div>
    </div>
  );
};

export default ArticleCard;
